/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { useQueryParameter } from '@/context/QueryParameter';
import type {
  ApprovingInvoiceReportColumn,
  ApprovingInvoiceReportsSearchQuery,
} from '@/context/services/approving_invoice_reports';
import { ApprovingInvoiceReport } from '@/features/approving_invoice_reports/components/ConfirmApproveModal/hooks/useBulkApproveReport';
import {
  MultiColumnSortState,
  SortField,
  SortOrder,
} from '@/features/approving_invoice_reports/components/List/Table/types';
import { useUpdateURLSearchParams } from '@/hooks/useConvertToURLSearchParams';
import { useQueryParams } from '@/hooks/useQueryParams';
import { useTranslation } from '@/i18n';
import { isEqual } from '@/utils/isEqual';
import {
  ApReportOrderByField,
  SearchApprovingInvoiceReportsQueryMfFile,
  SearchApprovingInvoiceReportsQueryStatus,
  SearchedApprovingInvoiceReport,
} from 'ap-openapi';
import { Dayjs } from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  convertFormDataToApiQuery,
  SearchFormData,
} from '../Search/hooks/useSearchForm';

export const useEmptyState = (isSearchEmpty: boolean) => {
  const { t } = useTranslation();

  return useMemo(() => {
    if (isSearchEmpty) {
      return {
        emptyType: 'search' as const,
        emptyMessage: t('approving_invoice_reports.table.no_search_results'),
      };
    }

    return {
      emptyType: 'object' as const,
      emptyMessage: t('approving_invoice_reports.table.empty_message'),
    };
  }, [isSearchEmpty, t]);
};

export const useResponseToApprovingInvoiceReportColumn = <
  T extends ApprovingInvoiceReportColumn
>(
  item: SearchedApprovingInvoiceReport
) => {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return {
    id: item.id,
    report_approval_id: item.report_approval_id,
    number: item.number,
    created_at: item.created_at,
    submitted_at: item.submitted_at,
    office_member: {
      name: item.office_member.name || '',
      number: item.office_member.number || '',
    },
    title: item.title,
    status: item.status || {},
    ap_payee_name: item.ap_payee_name,
    book_date: item.book_date,
    due_date: item.due_date,
    total_amount: item.total_amount,
    foreign_currency_amount: item.foreign_currency_amount,
    report_approval_names: item.report_approval_names,
    approved_at: item.approved_at,
    invoice_file: item.invoice_file
      ? {
          name: item.invoice_file.filename || '',
          receipt_type: item.invoice_file.receipt_type || '',
        }
      : undefined,
    automatic_status_waiting: item.automatic_status_waiting,
    timestamp_status_creating: item.timestamp_status_creating,
    error_messages: item.error_messages,
    detail_path: item.detail_path,
    is_editable: item.is_editable,
    is_report_approval: item.is_report_approval,
    is_waiting: item.is_waiting,
    is_approvable: item.is_approvable,
  } as unknown as T;
};

export const useTableSelection = (
  data: readonly ApprovingInvoiceReportColumn[]
) => {
  const [selectedRowData, setSelectedRowData] = useState<
    ApprovingInvoiceReport[]
  >([]);

  const selectedRowKeys = useMemo(() => {
    return selectedRowData.map((rowData) => rowData.report_id);
  }, [selectedRowData]);

  const selectableItems = useMemo(() => {
    return data.filter((item) => item.is_waiting);
  }, [data]);

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        const allKeys = data
          .filter((item) => item.is_waiting)
          .map((item) => ({
            report_id: item.id,
            report_approval_id: item.report_approval_id,
            number: item.number,
          }));
        setSelectedRowData(allKeys);
      } else {
        setSelectedRowData([]);
      }
    },
    [data, setSelectedRowData]
  );

  const handleSelectRow = useCallback(
    (record: ApprovingInvoiceReportColumn, checked: boolean) => {
      if (checked) {
        setSelectedRowData((prev) => [
          ...prev,
          {
            report_id: record.id,
            report_approval_id: record.report_approval_id,
            number: record.number,
          },
        ]);
      } else {
        setSelectedRowData((prev) =>
          prev.filter((rowData) => rowData.report_id !== record.id)
        );
      }
    },
    [setSelectedRowData]
  );

  const isAllSelected = useMemo(() => {
    return (
      selectableItems.length > 0 &&
      selectedRowData.length === selectableItems.length
    );
  }, [selectedRowData.length, selectableItems.length]);

  const isAllDisabled = useMemo(() => {
    return selectableItems.length === 0;
  }, [selectableItems.length]);

  return {
    selectedRowData,
    selectedRowKeys,
    handleSelectAll,
    handleSelectRow,
    isAllSelected,
    isAllDisabled,
  };
};

const formatDateRange = (
  from: string | Dayjs,
  to: string | Dayjs
): (string | Dayjs)[] | undefined => {
  if (!from || !to) {
    return ['', ''];
  }
  return [from, to];
};

export const mapColumnKeyToApiSortField = (
  field: SortField
): ApReportOrderByField => {
  const sortFieldMapping: Record<SortField, ApReportOrderByField> = {
    number: 'number',
    created_at: 'created_date',
    submitted_at: 'submitted_date',
    book_date: 'invoice_book_date',
    due_date: 'invoice_due_date',
    approved_at: 'approved_date',
  };
  return sortFieldMapping[field];
};

const mapApiSortFieldToColumnKey = (
  apiField: ApReportOrderByField | null
): string => {
  if (!apiField) return '';
  const reverseMapping: Record<string, string> = {
    created_date: 'created_at',
    submitted_date: 'submitted_at',
    invoice_due_date: 'due_date',
    invoice_book_date: 'book_date',
    approved_date: 'approved_at',
  };
  return reverseMapping[apiField] || apiField;
};

export const useApprovingInvoiceReportsFormInitial = (): SearchFormData => {
  const location = useLocation();
  const { getDateParam, getArrayParam } = useQueryParams();

  return useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const page = Number(searchParams.get('page')) || 1;
    const sortFields = searchParams.get('sort_fields');
    const sortOrders = searchParams.get('sort_orders');

    return {
      number: searchParams.get('number') || '',
      query: (searchParams.get('query') as string) || '',
      status:
        (searchParams.get(
          'status'
        ) as SearchApprovingInvoiceReportsQueryStatus) || '',
      ap_payee_id: (searchParams.get('ap_payee_id') as string) || '',
      created_date: formatDateRange(
        getDateParam(searchParams, 'created_at_from'),
        getDateParam(searchParams, 'created_at_to')
      ),
      submitted_date: formatDateRange(
        getDateParam(searchParams, 'submitted_at_from'),
        getDateParam(searchParams, 'submitted_at_to')
      ),
      due_date: formatDateRange(
        getDateParam(searchParams, 'due_date_from'),
        getDateParam(searchParams, 'due_date_to')
      ),
      approved_date: formatDateRange(
        getDateParam(searchParams, 'approved_at_from'),
        getDateParam(searchParams, 'approved_at_to')
      ),
      book_date: formatDateRange(
        getDateParam(searchParams, 'book_date_from'),
        getDateParam(searchParams, 'book_date_to')
      ),
      office_member_ids: getArrayParam(searchParams, 'office_member_ids') || [],
      dept_ids: getArrayParam(searchParams, 'dept_ids') || [],
      report_dept_ids: getArrayParam(searchParams, 'report_dept_ids') || [],
      total_amount_from: searchParams.get('total_amount_from') || '',
      total_amount_to: searchParams.get('total_amount_to') || '',
      mf_file:
        (searchParams.get(
          'mf_file'
        ) as SearchApprovingInvoiceReportsQueryMfFile) || '',
      page,
      without_agent: searchParams.get('without_agent') === 'true' || false,
      sort_fields: sortFields || undefined,
      sort_orders: sortOrders || undefined,
    };
  }, [getDateParam, getArrayParam, location.search]);
};
// Optimized search query hook with better state management and reduced re-renders
export const useApprovingInvoiceReportsHandleSearchQuery = (
  defaultValues: SearchFormData
) => {
  const [param] = useQueryParameter();
  const initialSearchQuery = useMemo(
    () => convertFormDataToApiQuery(defaultValues),
    [defaultValues]
  );

  const [searchQuery, setSearchQuery] =
    useState<ApprovingInvoiceReportsSearchQuery>(initialSearchQuery);

  // Derive sortOrders directly from URL parameters (computed value, always in sync)
  const sortOrders = useMemo(() => {
    // Use comma-separated format for both single and multiple sorts
    const sortFieldsParam = param.get('sort_fields'); // e.g., "created_date" or "created_date,submitted_date"
    const sortOrdersParam = param.get('sort_orders'); // e.g., "desc" or "desc,asc"

    // Parse sorts from comma-separated values
    if (sortFieldsParam && sortOrdersParam) {
      const fields = sortFieldsParam.split(',');
      const orders = sortOrdersParam.split(',');

      if (fields.length === orders.length) {
        return fields
          .map((field, index) => {
            const order = orders[index];
            if (field && order) {
              return {
                order: (order === 'asc' ? 'ascend' : 'descend') as
                  | 'ascend'
                  | 'descend',
                columnKey: mapApiSortFieldToColumnKey(
                  field as ApReportOrderByField
                ),
              };
            }
            return null;
          })
          .filter((s): s is MultiColumnSortState => s !== null);
      }
    }

    return [];
  }, [param]);

  const updateSearchParams = useUpdateURLSearchParams();

  // Simplified search handler - let React Query handle the rest
  const onSearch = useCallback(
    (
      newQuery: ApprovingInvoiceReportsSearchQuery,
      refetchTable: () => void
    ) => {
      const id = param.get('id') || undefined;
      const page = newQuery?.page;
      // Handle page-only updates
      if ('page' in newQuery && Object.keys(newQuery).length === 1) {
        const newSearchQuery = {
          ...searchQuery,
          page,
          sort_fields: searchQuery.sort_fields,
          sort_orders: searchQuery.sort_orders,
        };
        updateSearchParams({ ...newSearchQuery, id });
        setSearchQuery(newSearchQuery);
        return;
      }

      // Handle full search with filters
      const searchPayload = convertFormDataToApiQuery(newQuery);
      // Preserve sort_fields and sort_orders from newQuery since convertFormDataToApiQuery excludes them
      const completeSearchQuery = {
        ...searchPayload,
        page: 1,
        sort_fields: newQuery.sort_fields,
        sort_orders: newQuery.sort_orders,
      };
      updateSearchParams(completeSearchQuery);
      if (isEqual(searchQuery, completeSearchQuery)) {
        refetchTable();
      } else {
        setSearchQuery(completeSearchQuery);
      }
    },
    [param, updateSearchParams, searchQuery]
  );

  const handleSortChange = useCallback(
    (field: SortField, order: SortOrder) => {
      const transformedSortField = mapColumnKeyToApiSortField(field);

      const newSearchQuery: ApprovingInvoiceReportsSearchQuery = {
        ...searchQuery,
        sort_fields: order ? transformedSortField : undefined,
        sort_orders: order ? (order === 'ascend' ? 'asc' : 'desc') : undefined,
      };

      setSearchQuery(newSearchQuery);
      updateSearchParams(newSearchQuery as Record<string, unknown>);
    },
    [searchQuery, updateSearchParams]
  );

  // Multi-column sort handler - handles array of sort states
  const handleMultiColumnSort = useCallback(
    (sortStates: MultiColumnSortState[]) => {
      // Use comma-separated format for both single and multiple sorts
      const newSearchQuery: ApprovingInvoiceReportsSearchQuery = {
        ...searchQuery,
      };

      if (sortStates.length > 0) {
        // Convert all sort states to comma-separated URL parameters
        const sortFields = sortStates
          .map((s) => mapColumnKeyToApiSortField(s.columnKey as SortField))
          .join(',');
        const sortOrders = sortStates
          .map((s) => (s.order === 'ascend' ? 'asc' : 'desc'))
          .join(',');

        // Store in comma-separated format (works for single or multiple sorts)
        newSearchQuery.sort_fields = sortFields;
        newSearchQuery.sort_orders = sortOrders;
      } else {
        // Clear all sorting
        newSearchQuery.sort_fields = undefined;
        newSearchQuery.sort_orders = undefined;
      }

      setSearchQuery(newSearchQuery);
      updateSearchParams(newSearchQuery as Record<string, unknown>);
    },
    [searchQuery, updateSearchParams]
  );

  return {
    searchQuery,
    onSearch,
    handleSortChange,
    handleMultiColumnSort,
    sortOrders,
  };
};
